import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { useRef } from "react";

const features = [
  "Evaluación inicial de tu condición física",
  "Plan de entrenamiento adaptado a tus metas",
  "Ajustes según tu progreso cada semana",
  "Guía de alimentación complementaria",
  "Seguimiento constante de resultados",
];

export const ProgramSection = () => {
  const whatsappUrl = `https://wa.me/56966951278?text=${encodeURIComponent("¡Hola! Quiero saber más sobre el entrenamiento personalizado en Inspirion GYM")}`;
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

  return (
    <section 
      ref={sectionRef}
      id="entrenamiento"
      data-testid="program-section"
      className="relative py-24 bg-black overflow-hidden"
    >
      {/* Accent line */}
      <motion.div 
        className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-neon-green/50 to-transparent"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Image Side with Parallax */}
          <motion.div
            initial={{ opacity: 0, x: -80 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: "spring" }}
            className="flex-1 relative"
            style={{ y: imageY }}
          >
            <div className="relative">
              {/* Main image */}
              <motion.div 
                className="relative z-10 overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=80&w=2070"
                  alt="Entrenamiento personalizado"
                  className="w-full h-[500px] object-cover"
                  style={{ filter: "grayscale(30%) contrast(110%)" }}
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                
                {/* Animated border on hover */}
                <motion.div
                  className="absolute inset-0 border-2 border-neon-green/0"
                  whileHover={{ borderColor: "rgba(0, 255, 0, 0.5)" }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
              
              {/* Decorative frame with animation */}
              <motion.div 
                className="absolute -bottom-4 -right-4 w-full h-full border-2 border-neon-green/30 -z-0"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                style={{ boxShadow: "inset 0 0 20px rgba(0, 255, 0, 0.1)" }}
              />

              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.1, rotate: -5 }}
                className="absolute -bottom-6 -left-6 glass-card p-6 z-20 cursor-pointer"
              >
                <motion.div 
                  className="text-3xl font-black text-neon-green"
                  animate={{ 
                    textShadow: [
                      "0 0 10px rgba(0,255,0,0.5)",
                      "0 0 20px rgba(0,255,0,0.8)",
                      "0 0 10px rgba(0,255,0,0.5)",
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  100%
                </motion.div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">Personalizado</div>
              </motion.div>
            </div>
          </motion.div>

          {/* Content Side with Parallax */}
          <motion.div
            initial={{ opacity: 0, x: 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: "spring" }}
            className="flex-1"
            style={{ y: textY }}
          >
            <motion.span 
              className="text-neon-cyan text-sm font-bold uppercase tracking-widest"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Metodología Inspirion
            </motion.span>
            
            <motion.h2 
              className="mt-4 text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-tight"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Entrenamiento
              <br />
              <motion.span 
                className="text-neon-green italic"
                animate={{
                  textShadow: [
                    "0 0 10px rgba(0,255,0,0.5)",
                    "0 0 30px rgba(0,255,0,0.8)",
                    "0 0 10px rgba(0,255,0,0.5)",
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                Personalizado
              </motion.span>
            </motion.h2>
            
            <motion.p 
              className="mt-6 text-lg text-gray-300 leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Olvídate de las rutinas genéricas que no funcionan. En Inspirion GYM, 
              cada plan se crea específicamente para ti, considerando tus objetivos, 
              tu nivel actual, tu tiempo disponible y tu condición física.
            </motion.p>

            {/* Features list with staggered animation */}
            <ul className="mt-8 space-y-4">
              {features.map((feature, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-3 group cursor-pointer"
                >
                  <motion.div
                    whileHover={{ scale: 1.3, rotate: 10 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <CheckCircle2 className="w-5 h-5 text-neon-green flex-shrink-0 mt-0.5 group-hover:text-neon-cyan transition-colors" />
                  </motion.div>
                  <span className="text-gray-300 group-hover:text-white transition-colors">{feature}</span>
                </motion.li>
              ))}
            </ul>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="mt-10"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  asChild
                  data-testid="program-cta"
                  className="group bg-neon-green hover:bg-neon-green/90 text-black font-bold text-lg px-8 py-6 rounded-none uppercase tracking-wide"
                  style={{ boxShadow: "0 0 30px rgba(0, 255, 0, 0.4)" }}
                >
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                    Quiero mi plan personalizado
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                  </a>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProgramSection;
